/**
 * @file NotificationsMenu — dropdown anchored to the topbar bell.
 *
 * Surfaces actionable appointment events for the logged-in professional:
 * pending appointments awaiting confirmation, plus cancellations from the
 * last 7 days. Each item navigates to the appointment detail page.
 *
 * Until a dedicated Notifications table exists, this view is derived
 * client-side from the appointments list (already fetched by useAppointments).
 */
import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppointments } from "../hooks/useAppointments";

const Icon = {
  Pending: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  Cancelled: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </svg>
  ),
};

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

function patientLabel(appt) {
  const first = appt.patient?.user?.firstName || "";
  const last = appt.patient?.user?.lastName || "";
  const name = `${first} ${last}`.trim();
  return name || appt.attendeeName || "Paciente";
}

function formatDateTime(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("es-MX", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function relativeFromNow(value) {
  if (!value) return "";
  const diff = Date.now() - new Date(value).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `hace ${hr} h`;
  const day = Math.round(hr / 24);
  if (day < 7) return `hace ${day} d`;
  return new Date(value).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

export function deriveNotifications(appointments) {
  const cutoff = Date.now() - SEVEN_DAYS;
  const items = [];

  for (const a of appointments) {
    if (a.status === "PENDIENTE") {
      const ts = new Date(a.createdAt || a.updatedAt || a.dateTime).getTime();
      items.push({
        id: `pending-${a.id}`,
        appointmentId: a.id,
        kind: "PENDING",
        timestamp: ts,
        patientName: patientLabel(a),
        dateTime: a.dateTime,
      });
    } else if (a.status === "CANCELADA") {
      const ts = new Date(a.updatedAt || a.createdAt || a.dateTime).getTime();
      if (ts >= cutoff) {
        items.push({
          id: `cancelled-${a.id}`,
          appointmentId: a.id,
          kind: "CANCELLED",
          timestamp: ts,
          patientName: patientLabel(a),
          dateTime: a.dateTime,
          cancelReason: a.cancelReason,
        });
      }
    }
  }

  items.sort((x, y) => y.timestamp - x.timestamp);
  return items.slice(0, 12);
}

export function usePendingCount() {
  const { appointments } = useAppointments();
  return useMemo(
    () => appointments.filter((a) => a.status === "PENDIENTE").length,
    [appointments]
  );
}

export default function NotificationsMenu({ open, onClose, anchorRef }) {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const { appointments, isLoading } = useAppointments();

  const items = useMemo(() => deriveNotifications(appointments), [appointments]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onClick = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const handlePick = (item) => {
    onClose();
    navigate(`/cita/${item.appointmentId}`);
  };

  return (
    <div ref={panelRef} className="cd-notif-panel" role="dialog" aria-label="Notificaciones">
      <div className="cd-notif-head">
        <div className="cd-notif-title">Notificaciones</div>
      </div>
      {isLoading && items.length === 0 ? (
        <div className="cd-notif-empty">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="cd-notif-empty">No tienes notificaciones nuevas.</div>
      ) : (
        <ul className="cd-notif-list">
          {items.map((item) => (
            <li key={item.id}>
              <button type="button" className="cd-notif-item" onClick={() => handlePick(item)}>
                <span className={`cd-notif-icon cd-notif-icon-${item.kind === "PENDING" ? "warn" : "danger"}`}>
                  {item.kind === "PENDING" ? <Icon.Pending /> : <Icon.Cancelled />}
                </span>
                <span className="cd-notif-body">
                  <span className="cd-notif-line">
                    {item.kind === "PENDING"
                      ? `${item.patientName} reservó una cita`
                      : `${item.patientName} canceló su cita`}
                  </span>
                  <span className="cd-notif-meta">
                    {formatDateTime(item.dateTime)} · {relativeFromNow(item.timestamp)}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
